import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Loader2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { REVIEW_MODAL } from "../lib/testIds";
import { submitReview } from "../lib/api";

const StarRow = ({ value, hover, onSelect, onHover, onLeave }) => (
    <div
        className="flex items-center gap-1"
        onMouseLeave={onLeave}
    >
        {[1, 2, 3, 4, 5].map((n) => {
            const active = (hover || value) >= n;
            return (
                <button
                    key={n}
                    type="button"
                    data-testid={REVIEW_MODAL.star(n)}
                    onClick={() => onSelect(n)}
                    onMouseEnter={() => onHover(n)}
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    className="rounded-full p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                >
                    <Star
                        className={`h-7 w-7 ${
                            active ? "fill-[#d6a73f] text-[#d6a73f]" : "text-[#2e3220]/30"
                        }`}
                        strokeWidth={2}
                    />
                </button>
            );
        })}
    </div>
);

export const ReviewModal = ({ rental, open, onOpenChange, onSubmitted, revieweeName }) => {
    const initial = rental?.review || null;
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setRating(initial?.rating || 0);
            setComment(initial?.comment || "");
            setHover(0);
            setSubmitting(false);
        }
    }, [open, initial?.rating, initial?.comment]);

    if (!rental) return null;
    const displayName = revieweeName || rental.owner_name || "your neighbor";

    const handleSubmit = async () => {
        if (rating < 1) {
            toast.error("Pick a star rating to continue");
            return;
        }
        try {
            setSubmitting(true);
            const review = await submitReview(rental.id, {
                rating,
                comment: comment.trim(),
            });
            toast.success(initial ? "Review updated" : "Review submitted", {
                description: `Thanks for rating ${displayName}.`,
            });
            onSubmitted?.(review);
            onOpenChange(false);
        } catch (err) {
            toast.error("Couldn't submit your review", {
                description:
                    err?.response?.data?.detail || "Please try again in a moment.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={REVIEW_MODAL.root}
                className="max-w-md rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-7 text-[#2e3220]"
            >
                <DialogTitle className="font-display text-2xl font-bold text-[#2e3220]">
                    {initial ? "Edit your review" : "How was the rental?"}
                </DialogTitle>
                <DialogDescription
                    data-testid={REVIEW_MODAL.rentalLabel}
                    className="text-sm text-[#5a6142]"
                >
                    Reviewing <span className="font-semibold text-[#2e3220]">{displayName}</span> for{" "}
                    <span className="font-semibold text-[#2e3220]">{rental.item_name}</span>.
                </DialogDescription>

                <div className="mt-4">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                        Your rating
                    </p>
                    <div className="mt-2">
                        <StarRow
                            value={rating}
                            hover={hover}
                            onSelect={setRating}
                            onHover={setHover}
                            onLeave={() => setHover(0)}
                        />
                    </div>
                </div>

                <div className="mt-5">
                    <label
                        htmlFor="review-comment"
                        className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]"
                    >
                        Comment (optional)
                    </label>
                    <textarea
                        id="review-comment"
                        data-testid={REVIEW_MODAL.comment}
                        value={comment}
                        onChange={(e) => setComment(e.target.value.slice(0, 500))}
                        rows={4}
                        placeholder="Friendly, fair, item as described…"
                        className="mt-2 w-full resize-none rounded-2xl border border-[#2e3220]/15 bg-white/70 px-4 py-3 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                    />
                    <p className="mt-1 text-right text-[10px] text-[#5a6142]/70">
                        {comment.length}/500
                    </p>
                </div>

                <div className="mt-4 flex items-center justify-end gap-3">
                    <button
                        type="button"
                        data-testid={REVIEW_MODAL.cancel}
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                        className="rounded-full border border-[#2e3220]/15 bg-transparent px-5 py-2 text-sm font-semibold text-[#2e3220] hover:bg-[#2e3220]/5 disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-testid={REVIEW_MODAL.submit}
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="btn-primary disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                        {initial ? "Save changes" : "Submit review"}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ReviewModal;
